import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { Link, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { usePermissions } from "../hooks/usePermissions";
import {
  FaHome,
  FaShieldAlt,
  FaUsers,
  FaBook,
  FaOctopusDeploy,
  FaProductHunt,
  FaEnvelope,
  FaLayerGroup,
  FaImages,
  FaShoppingCart,
  FaTicketAlt,
  FaWallet,
  FaChartLine,
  FaBell,
} from "react-icons/fa";
import { MdContacts } from "react-icons/md";
import { FaRegCircleQuestion } from "react-icons/fa6";
import { FaMapLocationDot } from "react-icons/fa6";
import { TbCategory2 } from "react-icons/tb";
import { IoSchool } from "react-icons/io5";
import { FaUserShield } from "react-icons/fa";
import { FaCity } from "react-icons/fa";
import { FaChalkboardTeacher } from "react-icons/fa";
import { FcHome } from "react-icons/fc";
import { CiBarcode } from "react-icons/ci";
import { BiSolidCategoryAlt } from "react-icons/bi";
import { MdPrivacyTip } from "react-icons/md";
import { FaFileContract } from "react-icons/fa";

const ICON_STYLE = { fontSize: "20px", color: "#ffffff" };

const SideNavMenu = ({ handleLinkClick }) => {
  const { t } = useTranslation("global");
  const location = useLocation();
  const { hasPermission } = usePermissions();

  // استخدام Redux store للحصول على بيانات المستخدم
  const user = useSelector((state) => state.auth.user);

  // fallback للبيانات من sessionStorage في حالة عدم وجود بيانات في Redux store
  const getUserData = () => {
    if (user) return user;

    const storedUser = sessionStorage.getItem("loggedUser");
    if (storedUser) {
      try {
        return JSON.parse(storedUser);
      } catch {
        return null;
      }
    }
    return null;
  };

  const userData = getUserData();

  // تعريف القوائم: عناصر مستقلة (type: "link") وأقسام منسدلة (type: "group")
  const menuStructure = [
    {
      type: "link",
      path: "/",
      icon: <FaHome style={ICON_STYLE} />,
      text: t("sidenav.home"),
      permission: "view-dashboard",
    },
    {
      type: "group",
      key: "content",
      icon: <FaLayerGroup style={ICON_STYLE} />,
      text: "المحتوى",
      children: [
        {
          path: "/hero",
          icon: <FcHome style={ICON_STYLE} />,
          text: "السلايدر",
          permission: "view-sliders",
        },
        {
          path: "/slider-texts",
          icon: <FaImages style={ICON_STYLE} />,
          text: "البنرات",
        },
        {
          path: "/study-materials",
          icon: <FaBook style={ICON_STYLE} />,
          text: "المواد",
          permission: "view-subjects",
        },
        {
          path: "/sections",
          icon: <FaLayerGroup style={ICON_STYLE} />,
          text: "الأقسام",
          permission: "view-sections",
        },
        {
          path: "/category",
          icon: <BiSolidCategoryAlt style={ICON_STYLE} />,
          text: "الفئات",
          permission: "view-categories",
        },
        {
          path: "/cities",
          icon: <FaCity style={ICON_STYLE} />,
          text: "المدن",
          permission: "view-cities",
        },
        {
          path: "/regions",
          icon: <FaMapLocationDot style={ICON_STYLE} />,
          text: "المناطق",
          permission: "view-regions",
        },
      ],
    },
    {
      type: "group",
      key: "users",
      icon: <FaUsers style={ICON_STYLE} />,
      text: "المستخدمون",
      children: [
        {
          path: "/admins",
          icon: <FaUserShield style={ICON_STYLE} />,
          text: "الأدمن",
          permission: "view-admins",
        },
        {
          path: "/users",
          icon: <FaUsers style={ICON_STYLE} />,
          text: "المستخدمين",
          permission: "view-users",
        },
        {
          path: "/roles",
          icon: <FaShieldAlt style={ICON_STYLE} />,
          text: "الصلاحيات",
          permission: "view-roles",
        },
      ],
    },
    {
      type: "group",
      key: "students",
      icon: <IoSchool style={ICON_STYLE} />,
      text: "الطلبة",
      children: [
        {
          path: "/wallets",
          icon: <FaWallet style={ICON_STYLE} />,
          text: "المحافظ الإلكترونية",
          permission: "view-wallets",
        },
        {
          path: "/grades",
          icon: <IoSchool style={ICON_STYLE} />,
          text: "الصفوف",
          permission: "view-grades",
        },
      ],
    },
    {
      type: "group",
      key: "teachers",
      icon: <FaChalkboardTeacher style={ICON_STYLE} />,
      text: "المعلمين",
      children: [
        {
          path: "/teachers",
          icon: <FaUsers style={ICON_STYLE} />,
          text: "المعلمين",
          permission: "view-teachers",
        },
        {
          path: "/teachers_codes",
          icon: <CiBarcode style={ICON_STYLE} />,
          text: "الأكواد",
          permission: "get-teacher-codes",
        },
        {
          path: "/study-materials",
          icon: <FaBook style={ICON_STYLE} />,
          text: "المواد",
          permission: "view-subjects",
        },
      ],
    },
    {
      type: "group",
      key: "orders",
      icon: <FaShoppingCart style={ICON_STYLE} />,
      text: "الطلبات",
      children: [
        {
          path: "/orders",
          icon: <FaShoppingCart style={ICON_STYLE} />,
          text: "الطلبات",
          permission: "view-orders",
        },
        {
          path: "/coupons",
          icon: <FaTicketAlt style={ICON_STYLE} />,
          text: "الكوبونات",
        },
        {
          path: "/products",
          icon: <FaBook style={ICON_STYLE} />,
          text: "الكتب والأكواد",
          permission: "view-products",
        },
        {
          path: "/general-products",
          icon: <FaProductHunt style={ICON_STYLE} />,
          text: "المنتجات العامة",
          permission: "view-products",
        },
        {
          path: "/product-variants",
          icon: <TbCategory2 style={ICON_STYLE} />,
          text: "خصائص المنتجات",
          permission: "view-products",
        },
      ],
    },
    {
      type: "group",
      key: "site",
      icon: <FaOctopusDeploy style={ICON_STYLE} />,
      text: "الموقع",
      children: [
        {
          path: "/about-us",
          icon: <FaOctopusDeploy style={ICON_STYLE} />,
          text: "من نحن",
          permission: "manage-settings",
        },
        {
          path: "/features",
          icon: <TbCategory2 style={ICON_STYLE} />,
          text: "المميزات",
          permission: "view-features",
        },
        {
          path: "/faqs",
          icon: <FaRegCircleQuestion style={ICON_STYLE} />,
          text: "الاسئلة الشائعة",
          permission: "view-faqs",
        },
        {
          path: "/terms",
          icon: <FaFileContract style={ICON_STYLE} />,
          text: "الشروط والأحكام",
          permission: "update-terms",
        },
        {
          path: "/privacy",
          icon: <MdPrivacyTip style={ICON_STYLE} />,
          text: "سياسة الخصوصية",
          permission: "update-privacy",
        },
      ],
    },
    {
      type: "group",
      key: "treasuries",
      icon: <FaWallet style={ICON_STYLE} />,
      text: "الخزائن",
      children: [
        {
          path: "/admin-wallets",
          icon: <FaWallet style={ICON_STYLE} />,
          text: "كل خزائن خدمة العملاء",
          permission: "view-treasuries",
        },
        {
          path: "/transfers",
          icon: <FaWallet style={ICON_STYLE} />,
          text: "التحويلات والتوريدات",
          permission: "view-treasuries",
        },
      ],
    },
    {
      type: "link",
      path: "/contacts",
      icon: <MdContacts style={ICON_STYLE} />,
      text: t("sidenav.contacts"),
      permission: "view-contacts",
    },
    {
      type: "link",
      path: "/reports/daily-sales",
      icon: <FaChartLine style={ICON_STYLE} />,
      text: "المبيعات اليومية",
      permission: "view-sales-reports",
    },
    {
      type: "link",
      path: "/messages",
      icon: <FaEnvelope style={ICON_STYLE} />,
      text: "رسائل الإشعارات",
      permission: "view-messages",
    },
    {
      type: "link",
      path: "/notifications",
      icon: <FaBell style={ICON_STYLE} />,
      text: "الإشعارات (نظام جديد)",
      permission: "view-notifications",
    },
  ];

  // عنصر مسموح به إذا لم يكن له permission أو كان لدى المستخدم الصلاحية
  const isAllowed = (item) => !item.permission || hasPermission(item.permission);

  // بناء القائمة بعد تصفية الصلاحيات: القسم يظهر فقط إذا تبقى له عنصر واحد على الأقل
  const visibleMenu = menuStructure
    .map((item) => {
      if (item.type === "group") {
        const children = item.children.filter(isAllowed);
        return children.length ? { ...item, children } : null;
      }
      return isAllowed(item) ? item : null;
    })
    .filter(Boolean);

  // تحديد القسم الذي يحتوي على المسار الحالي (لفتحه تلقائياً)
  const activeGroupKey = visibleMenu.find(
    (item) =>
      item.type === "group" &&
      item.children.some((child) => child.path === location.pathname)
  )?.key;

  // حالة الأقسام المفتوحة
  const [openMenus, setOpenMenus] = useState(() =>
    activeGroupKey ? { [activeGroupKey]: true } : {}
  );

  // فتح القسم الذي يحتوي على الصفحة الحالية عند تغيّر المسار
  useEffect(() => {
    if (activeGroupKey) {
      setOpenMenus((prev) => ({ ...prev, [activeGroupKey]: true }));
    }
  }, [activeGroupKey]);

  const toggleMenu = (key) => {
    setOpenMenus((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  // إذا لم يكن هناك مستخدم، لا تعرض أي شيء
  if (!userData) {
    return null;
  }

  // عرض أيقونة العنصر مع لون مناسب لحالة التحديد
  const renderIcon = (iconElement, isActive) => {
    const IconComponent = iconElement.type;
    const iconColor = isActive ? "var(--sub-color)" : "var(--gray-color)";
    return (
      <IconComponent
        {...iconElement.props}
        style={{ ...iconElement.props.style, color: iconColor }}
      />
    );
  };

  const iconWrapperStyle = {
    marginRight: "0.25rem",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: "25px",
    height: "25px",
  };

  return (
    <ul className="mb-0 list-unstyled main-ul" style={{ gap: "1rem" }}>
      {visibleMenu.map((item) => {
        // عنصر منسدل (قسم)
        if (item.type === "group") {
          const isOpen = !!openMenus[item.key];
          const hasActiveChild = item.children.some(
            (child) => child.path === location.pathname
          );

          return (
            <li
              key={item.key}
              className={hasActiveChild ? "active" : ""}
              style={{ position: "relative", display: "block" }}
            >
              <div
                onClick={() => toggleMenu(item.key)}
                style={{
                  display: "flex",
                  alignItems: "center",
                  cursor: "pointer",
                }}
              >
                <div className="icon" style={iconWrapperStyle}>
                  {renderIcon(item.icon, hasActiveChild)}
                </div>
                <span
                  className={`dropdown-toggle ${isOpen ? "open" : ""}`}
                  style={{ cursor: "pointer" }}
                >
                  <span>{item.text}</span>
                </span>
              </div>

              <ul
                className={`dropdown-menu list-unstyled ${isOpen ? "show" : ""}`}
                style={{ display: isOpen ? "block" : "none" }}
              >
                {item.children.map((child) => {
                  const isActive = location.pathname === child.path;
                  return (
                    <li
                      key={child.path + child.text}
                      className={isActive ? "active" : ""}
                      style={{ position: "relative" }}
                    >
                      <div className="icon" style={iconWrapperStyle}>
                        {renderIcon(child.icon, isActive)}
                      </div>
                      <Link
                        to={child.path}
                        className="dropdown-item"
                        onClick={handleLinkClick}
                      >
                        <span>{child.text}</span>
                      </Link>
                    </li>
                  );
                })}
              </ul>
            </li>
          );
        }

        // عنصر مستقل (لينك مباشر)
        const isActive = location.pathname === item.path;
        return (
          <li
            key={item.path}
            className={isActive ? "active" : ""}
            style={{ marginBottom: "0.5rem", position: "relative" }}
          >
            <div className="icon" style={iconWrapperStyle}>
              {renderIcon(item.icon, isActive)}
            </div>
            <Link to={item.path} onClick={handleLinkClick}>
              <span>{item.text}</span>
            </Link>
          </li>
        );
      })}
    </ul>
  );
};

export default SideNavMenu;
